import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Chip, Card, Stepper, Frame, IconChip,
} from '../../../components/onboarding/primitives';
import { publicAPI, paymentOrdersAPI } from '../../../services/api';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع'];

// Small helper to pick a default icon per plan slug (fallback only —
// backend may embed an icon name we don't know about).
const ICON_FOR_SLUG = {
  free: <Icon.Clock />,
  trial: <Icon.Clock />,
  basic: <Icon.Flask />,
  starter: <Icon.Flask />,
  professional: <Icon.Spark />,
  pro: <Icon.Spark />,
  enterprise: <Icon.Crown />,
  business: <Icon.Crown />,
};

const getPlanIcon = (slug) => ICON_FOR_SLUG[slug] || <Icon.Flask />;

/**
 * Props contract: { state, setState, goToStep, goBack }
 */
export default function Step4Plan({ state, setState, goToStep, goBack }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const selectedSlug = state.s4?.plan || null;
  const cycle = 'yearly'; // yearly-only in this flow

  const branchCount = (state.s3?.branches || []).length;

  // Fetch plans from public endpoint, filter out 'free', sort by sortOrder.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await publicAPI.getPlans();
        const raw = res?.data?.data || res?.data?.plans || res?.data || [];
        // Show every active plan returned by the public endpoint. The
        // backend already gates which plans are exposed (admin toggles
        // `isActive` on each plan). The legacy `slug !== 'free'` filter
        // hid the trial plan from new sign-ups, but the trial is now a
        // legitimate first-step option for labs that want to evaluate.
        const list = (Array.isArray(raw) ? raw : [])
          .filter((p) => p && (p.isActive !== false))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        if (cancelled) return;
        setPlans(list);

        // Set default selection if none chosen yet.
        if (!state.s4?.plan && list.length) {
          const popular = list.find((p) => p.isPopular);
          const defaultSlug = list.find((p) => p.slug === 'professional')?.slug
            || popular?.slug
            || list[0].slug;
          setState({ ...state, s4: { plan: defaultSlug, cycle: 'yearly' } });
        }
      } catch (err) {
        setFetchError(err?.response?.data?.message || err?.message || 'تعذّر تحميل الباقات');
        toast.error('تعذّر تحميل الباقات — تأكد من اتصال الخادم');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.slug === selectedSlug) || null,
    [plans, selectedSlug],
  );

  const setPlan = (slug) => setState({ ...state, s4: { plan: slug, cycle: 'yearly' } });

  const handleSubmit = async () => {
    if (!selectedPlan || submitting) return;
    setSubmitting(true);
    // Keep state in sync — also stash the full plan doc on state.s4.planDoc
    // so Step 5 (Devices) can read `plan.limits.maxDevices` and the unit
    // price without a second fetch. The `plan` field (slug) stays for back-
    // compat with any consumer that expected it.
    setState({
      ...state,
      s4: {
        plan: selectedPlan.slug,
        planDoc: selectedPlan,
        cycle: 'yearly',
      },
    });

    try {
      await paymentOrdersAPI.updateDraft({
        planId: selectedPlan._id,
        billingCycle: 'yearly',
        branchCount,
      });
      goToStep(5);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // Endpoint not deployed yet — don't block progress.
        toast('متابعة بدون تسجيل المسودّة (خدمة الدفع قيد الإعداد)', { icon: 'i' });
        goToStep(5);
        return;
      }
      const msg = err?.response?.data?.message
        || err?.response?.data?.error
        || 'تعذّر حفظ اختيار الباقة. حاول مرة أخرى.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => { if (goBack) goBack(); else goToStep(3); };

  return (
    <Frame
      maxWidth={1200}
      stepper={<Stepper steps={STEP_LABELS} current={3} />}
      footer={
        <>
          <Button variant="outline" onClick={onBack} iconLeft={<Icon.Arrow dir="right" />}>السابق</Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedPlan || submitting}
            icon={submitting ? <Icon.Clock size={16} /> : <Icon.Arrow />}
          >
            {submitting ? 'جارٍ الحفظ...' : 'متابعة للدفع'}
          </Button>
        </>
      }
    >
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, margin: '0 0 10px', letterSpacing: -0.6 }}>
          اختر الباقة المناسبة لمختبرك
        </h1>
        <p style={{ fontSize: 15, color: BRAND.muted, margin: 0 }}>
          كل الباقات سنوية مع خصم مقارنة بالشهري
        </p>

        <div style={{
          display: 'inline-flex', marginTop: 24,
          background: BRAND.surfaceAlt, padding: 5, borderRadius: 12,
          border: `1px solid ${BRAND.border}`,
        }}>
          <div style={{
            padding: '8px 20px', background: '#fff', borderRadius: 8,
            fontSize: 13.5, fontWeight: 600, color: BRAND.text,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            سنوي <Chip tone="mint">وفّر ~20%</Chip>
          </div>
          <div style={{ padding: '8px 20px', fontSize: 13.5, color: BRAND.muted, opacity: 0.5 }}>
            شهري — قريباً
          </div>
        </div>
      </div>

      {loading ? (
        <Card padding={48} style={{ textAlign: 'center', color: BRAND.muted }}>
          جارٍ تحميل الباقات...
        </Card>
      ) : fetchError ? (
        <Card padding={48} style={{ textAlign: 'center', color: BRAND.error }}>
          {fetchError}
        </Card>
      ) : plans.length === 0 ? (
        <Card padding={48} style={{ textAlign: 'center', color: BRAND.muted }}>
          لا توجد باقات متاحة حالياً.
        </Card>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)`,
          gap: 20,
        }}>
          {plans.map((p) => (
            <PlanCard
              key={p._id || p.slug}
              plan={p}
              selected={selectedSlug === p.slug}
              onSelect={() => setPlan(p.slug)}
            />
          ))}
        </div>
      )}

    </Frame>
  );
}

// ─── PlanCard ───────────────────────────────────────────
function PlanCard({ plan, selected, onSelect }) {
  const yearly = Number(plan.yearlyPrice) || 0;
  const monthly = Number(plan.monthlyPrice) || 0;
  const monthlyEquiv = yearly > 0 ? Math.round(yearly / 12) : 0;
  const savings = Math.max(0, (monthly * 12) - yearly);

  const name = plan.nameAr || plan.name || plan.slug;
  const tagline = plan.descriptionAr || plan.description || '';

  // Only the highlight points authored by an admin in plan-management are
  // surfaced here — NEVER the technical `features` flags (maxBranches etc.)
  // since those are marketing-hostile and the customer is billed per branch.
  const highlightPoints = Array.isArray(plan.highlightPoints)
    ? plan.highlightPoints
    : Array.isArray(plan.highlights)
      ? plan.highlights
      : [];

  return (
    <div
      onClick={onSelect}
      style={{
        position: 'relative', background: '#fff',
        border: `${selected ? 2 : 1}px solid ${selected ? BRAND.green : BRAND.border}`,
        borderRadius: 20, padding: 28, cursor: 'pointer',
        boxShadow: selected
          ? '0 20px 50px -20px rgba(15,110,86,0.3)'
          : '0 1px 2px rgba(0,0,0,0.03)',
        transition: 'all .2s',
      }}
    >
      {plan.isPopular && (
        <div style={{
          position: 'absolute', top: -12, insetInlineEnd: 20,
          background: `linear-gradient(135deg, ${BRAND.greenDark}, ${BRAND.green})`,
          color: '#fff', fontSize: 11, fontWeight: 700,
          padding: '5px 12px', borderRadius: 999, letterSpacing: 0.3,
        }}>
          الأكثر طلباً
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <IconChip size={44}>{getPlanIcon(plan.slug)}</IconChip>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.3 }}>{name}</div>
          {tagline && (
            <div style={{
              fontSize: 12, color: BRAND.muted, marginTop: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {tagline}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{
            fontSize: 40, fontWeight: 800, color: BRAND.text,
            letterSpacing: -1, fontVariantNumeric: 'tabular-nums',
          }}>
            {yearly.toLocaleString()}
          </span>
          <span style={{ fontSize: 13, color: BRAND.muted }}>ر.س / سنة</span>
        </div>
        {monthlyEquiv > 0 && (
          <div style={{ fontSize: 12.5, color: BRAND.muted, marginTop: 4 }}>
            ≈ {monthlyEquiv.toLocaleString()} ر.س/شهر
          </div>
        )}
        {savings > 0 && (
          <div style={{ marginTop: 10 }}>
            <Chip tone="mint">توفير {savings.toLocaleString()} ر.س</Chip>
          </div>
        )}
      </div>

      {highlightPoints.length > 0 && (
        <div style={{ borderTop: `1px solid ${BRAND.border}`, margin: '20px 0', paddingTop: 20 }}>
          <ul style={{
            listStyle: 'none', padding: 0, margin: 0,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {highlightPoints.map((h, i) => (
              <li
                key={i}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  fontSize: 13.5, color: BRAND.text, lineHeight: 1.5,
                }}
              >
                <span style={{ color: BRAND.green, marginTop: 2, flexShrink: 0 }}>
                  <Icon.Check size={15} />
                </span>
                {typeof h === 'string' ? h : (h.text || h.textAr || h.label || '')}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        style={{
          width: '100%', padding: 12, borderRadius: 12,
          fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
          cursor: 'pointer',
          background: selected
            ? `linear-gradient(135deg, ${BRAND.greenDark}, ${BRAND.green})`
            : '#fff',
          color: selected ? '#fff' : BRAND.text,
          border: selected ? 'none' : `1px solid ${BRAND.border}`,
          boxShadow: selected ? '0 8px 20px rgba(29,158,117,0.25)' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}
      >
        {selected ? <><Icon.Check size={14} /> مُختارة</> : 'اختر هذه الباقة'}
      </button>
    </div>
  );
}
